import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProjectsPage = () => {
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const params = filter !== "all" ? { category: filter } : {};
        const response = await axios.get(`${API}/projects`, { params });
        setProjects(response.data);
      } catch (error) {
        console.error("Error fetching projects:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, [filter]);

  const filters = [
    { value: "all", label: "All" },
    { value: "bathroom", label: "Bathrooms" },
    { value: "kitchen", label: "Kitchens" },
    { value: "floor", label: "Floors" },
    { value: "feature_wall", label: "Feature Walls" }
  ];

  return (
    <div data-testid="projects-page">
      {/* Hero */}
      <section className="py-20 bg-gray-50 grid-pattern">
        <div className="container-custom text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 font-montserrat">
            Our Projects
          </h1>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
            Explore our portfolio of completed tiling installations across Greater Manchester.
          </p>
        </div>
      </section>

      {/* Filter Tabs */}
      <section className="py-8 bg-white border-b border-gray-100">
        <div className="container-custom">
          <div className="flex flex-wrap justify-center gap-2">
            {filters.map((f) => (
              <button
                key={f.value}
                onClick={() => setFilter(f.value)}
                className={`px-6 py-2 rounded-full font-medium transition-all ${
                  filter === f.value
                    ? "bg-primary text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
                data-testid={`project-filter-${f.value}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="section-padding">
        <div className="container-custom">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="space-y-4">
                  <div className="aspect-[4/3] skeleton rounded-xl" />
                  <div className="h-6 skeleton rounded w-3/4" />
                  <div className="h-4 skeleton rounded w-1/2" />
                </div>
              ))}
            </div>
          ) : projects.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {projects.map((project, index) => (
                <Link 
                  to={`/projects/${project.id}`}
                  key={project.id}
                  className="group"
                  data-testid={`project-item-${index}`}
                >
                  <div className="aspect-[4/3] rounded-xl overflow-hidden mb-4">
                    <img
                      src={project.images?.[0] || "https://images.unsplash.com/photo-1758548157126-e4c0477f796e?w=600"}
                      alt={project.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-primary transition-colors font-montserrat">
                    {project.title}
                  </h3>
                  <div className="flex items-center gap-2 text-gray-500">
                    <MapPin className="w-4 h-4" />
                    <span>{project.location}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-sm bg-gray-100 text-gray-600 px-3 py-1 rounded-full">
                      {project.tile_type}
                    </span>
                    <span className="text-sm bg-gray-100 text-gray-600 px-3 py-1 rounded-full">
                      {project.area_tiled}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500 mb-6">No projects found in this category.</p>
              <Link to="/quote">
                <Button className="bg-primary hover:bg-primary/90">
                  Start Your Project
                </Button>
              </Link>
            </div>
          )}

          {/* CTA */}
          <div className="text-center mt-12">
            <Link to="/quote">
              <Button size="lg" className="bg-primary hover:bg-primary/90" data-testid="projects-cta">
                Get a Quote For Your Project
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProjectsPage;
